import { useState } from 'react'
import { StatCard } from '@/components/StatCard'
import { ErrorState, StatCardSkeleton } from '@/components/States'
import { useActivity, useSecuritySummary } from '@/hooks/useRootQueries'
import { DataTable, type Column } from '@/components/DataTable'
import { StatusBadge } from '@/components/StatusBadge'
import { IconSecurity, IconAlertTriangle, IconOrganizations } from '@/components/Icons'
import { DocumentDetailDrawer } from '@/components/DocumentDetailDrawer'
import type { AuditEvent } from '@/types/api'
import { exactTimestamp, relativeTime } from '@/utils/time'

export function Security() {
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null)
  const summary = useSecuritySummary()
  const failures = useActivity({ status: 'failure', page: 1, pageSize: 20 })

  const columns: Column<AuditEvent>[] = [
    {
      key: 'timestamp',
      header: 'Event Timestamp',
      render: (row) => (
        <div>
          <div className="font-semibold text-slate-900">{relativeTime(row.timestamp)}</div>
          <div className="font-mono text-[10px] text-slate-500">{exactTimestamp(row.timestamp)}</div>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Security Incident / Action',
      render: (row) => (
        <div>
          <span className="font-semibold text-slate-900 bg-rose-50/70 border border-rose-200 px-2.5 py-1 rounded-md text-xs inline-flex items-center gap-1.5 text-rose-800">
            <IconAlertTriangle className="h-3.5 w-3.5 text-rose-600" />
            <span>{row.action}</span>
          </span>
          {row.documentTitle && (
            <div className="mt-1 font-sans text-xs font-semibold text-slate-700 truncate max-w-[200px]">
              📄 {row.documentTitle}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'org',
      header: 'Organization',
      render: (row) => {
        const orgName = row.orgName || (row.orgId ? `Org #${row.orgId.slice(-6)}` : 'Platform System')
        return (
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded bg-slate-100 text-slate-600 border border-slate-200">
              <IconOrganizations className="h-3.5 w-3.5" />
            </div>
            <span className="text-xs font-semibold text-slate-800">{orgName}</span>
          </div>
        )
      },
    },
    {
      key: 'actor',
      header: 'Target / Actor Identity',
      render: (row) => (
        <div>
          <div className="font-semibold text-slate-900">{row.actor || row.actorName || 'System Scheduler'}</div>
          {row.actorEmail && <div className="font-mono text-[10.5px] text-slate-500">{row.actorEmail}</div>}
        </div>
      ),
    },
    {
      key: 'ip',
      header: 'Source IP Address',
      render: (row) => {
        if (row.ipAddress) {
          return (
            <span className="font-mono text-slate-800 font-semibold text-[11px] bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
              {row.ipAddress}
            </span>
          )
        }
        const isSystem = (row.actor || row.actorName || '').toLowerCase().includes('system') || !row.actorEmail
        if (isSystem) {
          return (
            <span className="inline-flex items-center gap-1 rounded bg-slate-100 border border-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
              <span>⚙️</span>
              <span>System Internal</span>
            </span>
          )
        }
        return <span className="text-slate-400 text-xs">—</span>
      },
    },
    {
      key: 'severity',
      header: 'Risk Level',
      render: (row) => {
        const act = (row.action || '').toLowerCase()
        const isCrit = act.includes('lock') || act.includes('fail') || act.includes('unauthor') || act.includes('suspicious')
        return <StatusBadge status={isCrit ? 'crit' : 'warn'} label={isCrit ? 'critical' : 'warning'} />
      },
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <span>Security Center &amp; Threat Intelligence</span>
          </h1>
          <p className="mt-0.5 text-xs font-medium text-slate-500">
            Real-time attack telemetry, failed authentication attempts, rate limiting, and incident logging.
          </p>
        </div>
      </div>

      {summary.isError && <ErrorState message="Could not load the security summary." onRetry={() => summary.refetch()} />}

      {!summary.isError && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {summary.isLoading || !summary.data ? (
            Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)
          ) : (
            <>
              <StatCard
                label="Failed Logins Today"
                value={summary.data.failedLoginsToday}
                icon={<IconSecurity className="h-4 w-4" />}
                delta={summary.data.failedLoginsToday > 0 ? "Potential brute-force" : "Normal"}
                tone={summary.data.failedLoginsToday > 0 ? 'crit' : 'neutral'}
              />
              <StatCard
                label="Locked Accounts"
                value={summary.data.lockedAccounts}
                icon={<IconAlertTriangle className="h-4 w-4" />}
                delta={summary.data.lockedAccounts > 0 ? "Requires review" : "Zero locks"}
                tone={summary.data.lockedAccounts > 0 ? 'warn' : 'neutral'}
              />
              <StatCard
                label="Suspicious Events"
                value={summary.data.suspiciousEvents}
                icon={<IconAlertTriangle className="h-4 w-4" />}
                delta="Anomalous IPs"
                tone={summary.data.suspiciousEvents > 0 ? 'warn' : 'neutral'}
              />
              <StatCard
                label="Unique IPs Today"
                value={summary.data.uniqueIpsToday}
                icon={<IconSecurity className="h-4 w-4" />}
                delta="Cluster ingress"
                tone="neutral"
              />
            </>
          )}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white shadow-xs overflow-hidden">
        <div className="border-b border-slate-200 bg-slate-50/80 px-5 py-3.5 flex items-center justify-between">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">
            Recent Security Incidents &amp; Authentication Failures
          </h2>
          <span className="font-mono text-xs font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
            Threat Stream
          </span>
        </div>
        <DataTable
          columns={columns}
          rows={failures.data?.data ?? []}
          getRowId={(row) => row.id}
          isLoading={failures.isLoading}
          isError={failures.isError}
          errorMessage="Could not load security events."
          onRetry={() => failures.refetch()}
          emptyTitle="No recent security alerts"
          emptyDescription="No malicious activity or authentication failures recorded in this period."
          onRowClick={(row) => setSelectedEvent(row)}
        />
      </div>

      {/* Security Event & Document Detail Drawer */}
      <DocumentDetailDrawer
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        event={selectedEvent}
        documentId={selectedEvent?.documentId || selectedEvent?.document_id}
      />
    </div>
  )
}
